import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import jsPDF from "jspdf";
import {
  Search, Instagram, ArrowLeft, Loader2, Star, Shield, TrendingUp, Hash, Target, Zap,
  ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, Users, Download, Ghost, FileText,
  Swords, Trophy, Lightbulb, ArrowUpRight, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { proxyImage } from "@/lib/img";
import BrandDnaCard from "@/components/app/BrandDnaCard";
import { useBrandDnaSummary } from "@/lib/useBrandDna";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ABLEFY_LIFETIME = "https://myablefy.com/s/erfolgbeginntindir/socialiq/payment";

function ScoreCircle({ score, size = "lg" }) {
  const color = score >= 70 ? "text-emerald-400" : score >= 40 ? "text-amber-400" : "text-red-400";
  const bg = score >= 70 ? "bg-emerald-500/10" : score >= 40 ? "bg-amber-500/10" : "bg-red-500/10";
  const s = size === "lg" ? "w-20 h-20" : "w-12 h-12";
  const ts = size === "lg" ? "text-2xl" : "text-sm";
  return (
    <div className={`${s} ${bg} rounded-full flex items-center justify-center`}>
      <span className={`${ts} font-bold ${color}`}>{score}</span>
    </div>
  );
}

function Section({ title, icon: Icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl bg-zinc-900 border border-zinc-800 overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 hover:bg-zinc-800/50 transition-colors">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-semibold text-white">{title}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </button>
      {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  );
}

function ListItems({ items, color = "text-zinc-300" }) {
  if (!items || !items.length) return null;
  return (
    <ul className="space-y-1">
      {items.map((item, i) => (
        <li key={i} className={`text-xs ${color} flex items-start gap-2`}>
          <span className="text-zinc-600 mt-0.5">-</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function PriorityBadge({ priority }) {
  const map = {
    hoch: "bg-red-500/15 text-red-400 border-red-500/30",
    mittel: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    niedrig: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  };
  const label = { hoch: "Hoch", mittel: "Mittel", niedrig: "Niedrig" };
  const cls = map[priority] || map.mittel;
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md border ${cls}`}>
      {label[priority] || priority}
    </span>
  );
}

function PaywallOverlay() {
  return (
    <div className="absolute inset-0 z-30 flex items-end justify-center pointer-events-none" data-testid="paywall-overlay">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#09090b]/60 to-[#09090b]" />
      <div className="relative pointer-events-auto p-6 max-w-md text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4" style={{ borderColor: 'rgba(0,212,255,0.4)', background: 'rgba(0,212,255,0.10)', borderWidth: 1, borderStyle: 'solid' }}>
          <Sparkles className="w-3.5 h-3.5" style={{ color: '#00D4FF' }} />
          <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#7DD8F4' }}>Premium-Inhalt</span>
        </div>
        <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Schalte deinen vollen Audit frei</h3>
        <p className="text-sm text-zinc-300 mb-2">Bio-Optimierung, Content & Hashtag-Strategie, Compliance-Check, Algorithmus-Tipps, Konkurrenz-Vergleich, 16-Item-Checkliste, PDF-Download — und unlimitierte Karussell-Generierung.</p>
        <p className="text-xs font-semibold mb-4" style={{ color: '#C8FF4D' }}>✓ Einmal 97 € — Lifetime-Zugang. Kein Abo, keine Kündigung.</p>
        <a
          href={ABLEFY_LIFETIME}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block px-6 py-3 rounded-xl text-black text-sm font-bold transition-all hover:scale-105"
          style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)' }}
          data-testid="paywall-lifetime-btn"
        >
          Jetzt Lifetime sichern · 97 €
        </a>
      </div>
    </div>
  );
}

export default function AnalysePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isPremium = !!user?.plan_active;
  const isFreeTrialMode = !isPremium;
  const [platform, setPlatform] = useState(searchParams.get("platform") === "tiktok" ? "tiktok" : "instagram");
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [analysisId, setAnalysisId] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState({});
  const { summary: dna } = useBrandDnaSummary();

  // Competitor comparison state
  const [competitors, setCompetitors] = useState(null); // {competitors:[], note:""} | null
  const [loadingCompetitors, setLoadingCompetitors] = useState(false);
  const [compareId, setCompareId] = useState(null);
  const [compare, setCompare] = useState(null);
  const [loadingCompare, setLoadingCompare] = useState(false);

  // Search on button click or Enter
  const searchProfiles = useCallback(async () => {
    if (query.length < 2) { setSuggestions([]); return; }
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/analyse/search?q=${encodeURIComponent(query)}&platform=${platform}`);
      setSuggestions(data);
    } catch { setSuggestions([]); }
    finally { setSearching(false); }
  }, [query, platform]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // Enter triggers direct analysis (faster than Apify search)
      if (query.length >= 2) startAnalysis(query.trim().replace(/^@/, ""));
    }
  };

  // Poll analysis
  useEffect(() => {
    if (!analysisId) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/analyse/${analysisId}`);
        setAnalysis(data);
        if (data.status === "completed" || data.status === "error") {
          clearInterval(interval);
          if (data.status === "completed") toast.success("Analyse fertig!");
          if (data.status === "error") toast.error("Analyse fehlgeschlagen");
        }
      } catch { clearInterval(interval); }
    }, 3000);
    return () => clearInterval(interval);
  }, [analysisId]);

  const startAnalysis = async (username) => {
    // Free-trial gating: 1 audit per visitor (localStorage)
    if (isFreeTrialMode) {
      const usedFreeAnalyse = localStorage.getItem("vc_free_analyse_used");
      if (usedFreeAnalyse) {
        toast.error("Deine kostenlose Analyse wurde bereits verwendet. Werde Premium für unlimited Audits!");
        navigate("/#pricing");
        return;
      }
    }
    setLoading(true);
    setSuggestions([]);
    setQuery(username);
    setChecked({});
    setCompetitors(null);
    setCompareId(null);
    setCompare(null);
    try {
      const { data } = await axios.post(`${API}/analyse/run`, { username, platform });
      setAnalysisId(data.id);
      setAnalysis(data);
      if (isFreeTrialMode) {
        localStorage.setItem("vc_free_analyse_used", "1");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analyse fehlgeschlagen");
    } finally { setLoading(false); }
  };

  // ── Competitor flow ──
  const loadCompetitors = async () => {
    if (!analysisId) return;
    setLoadingCompetitors(true);
    try {
      const { data } = await axios.post(`${API}/analyse/${analysisId}/competitors`);
      setCompetitors(data);
      toast.success(`${data.competitors?.length || 0} Konkurrent:innen vorgeschlagen`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vorschläge fehlgeschlagen");
    } finally { setLoadingCompetitors(false); }
  };

  const startCompare = async (competitorUsername) => {
    setLoadingCompare(true);
    try {
      const { data } = await axios.post(`${API}/analyse/compare`, {
        analysis_id: analysisId,
        competitor_username: competitorUsername,
      });
      setCompareId(data.id);
      setCompare(data);
      toast.info(`Vergleich gestartet: @${data.you_username} vs @${data.competitor_username}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vergleich konnte nicht gestartet werden");
    } finally { setLoadingCompare(false); }
  };

  // Poll compare
  useEffect(() => {
    if (!compareId) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/analyse/compare/${compareId}`);
        setCompare(data);
        if (data.status === "completed" || data.status === "error") {
          clearInterval(interval);
          if (data.status === "completed") toast.success("Konkurrenz-Vergleich fertig!");
          if (data.status === "error") toast.error(data.error_message || "Vergleich fehlgeschlagen");
        }
      } catch { clearInterval(interval); }
    }, 3000);
    return () => clearInterval(interval);
  }, [compareId]);

  const ai = analysis?.ai_analysis || {};
  const profile = analysis?.profile_data || {};

  const downloadPdf = () => {
    if (!ai || !profile) return;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const margin = 40;
    const w = doc.internal.pageSize.getWidth();
    let y = margin;

    const writeLine = (txt, opts = {}) => {
      const fontSize = opts.size || 11;
      const bold = opts.bold || false;
      const color = opts.color || [30, 30, 30];
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(fontSize);
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(txt, w - margin * 2);
      lines.forEach(l => {
        if (y > 780) { doc.addPage(); y = margin; }
        doc.text(l, margin, y);
        y += fontSize * 1.35;
      });
      y += 4;
    };

    // Header
    doc.setFillColor(236, 72, 153);
    doc.rect(0, 0, w, 60, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("ViralCut AI — Profil-Audit", margin, 38);
    y = 90;

    writeLine(`@${profile.username}  •  ${platform.toUpperCase()}`, { size: 14, bold: true });
    writeLine(`${profile.full_name || ""}`, { size: 11, color: [80, 80, 80] });
    writeLine(`Follower: ${(profile.followers || 0).toLocaleString()}  •  Following: ${(profile.following || 0).toLocaleString()}  •  Beitraege: ${(profile.posts_count || profile.videos_count || 0).toLocaleString()}`, { size: 10, color: [100, 100, 100] });
    y += 6;

    writeLine(`Gesamt-Score: ${ai.overall_score || 0}/100`, { size: 14, bold: true, color: [236, 72, 153] });
    writeLine(ai.overall_verdict || "", { size: 11 });

    if (ai.follower_quality) {
      y += 8;
      writeLine("Follower-Qualitaet", { size: 13, bold: true, color: [236, 72, 153] });
      writeLine(`Score: ${ai.follower_quality.score}/100  •  Engagement-Rate: ${ai.follower_quality.engagement_rate}%`, { size: 10 });
      writeLine(`Geschaetzte Ghost-Follower: ${ai.follower_quality.ghost_follower_pct}% (~${(ai.follower_quality.ghost_follower_estimate || 0).toLocaleString()} Accounts)`, { size: 10, color: [200, 50, 50] });
      writeLine(`Aktive Follower: ~${(ai.follower_quality.active_followers_estimate || 0).toLocaleString()}`, { size: 10, color: [50, 150, 80] });
      writeLine(ai.follower_quality.verdict || "", { size: 10 });
      if (ai.follower_quality.warning) writeLine(`WARNUNG: ${ai.follower_quality.warning}`, { size: 10, color: [200, 50, 50] });
    }

    // Sections summary
    const sections = [
      ["Bio & Profil", ai.bio_analysis],
      ["Content & Engagement", ai.content_analysis],
      ["Hashtag-Strategie", ai.hashtag_analysis],
      ["Compliance", ai.compliance_check],
      ["Algorithmus", ai.algorithm_tips],
      ["Wachstumsstrategie", ai.growth_strategy],
    ];
    sections.forEach(([title, data]) => {
      if (!data) return;
      y += 6;
      writeLine(title, { size: 13, bold: true, color: [236, 72, 153] });
      if (data.score !== undefined) writeLine(`Score: ${data.score}/100`, { size: 10 });
      Object.entries(data).forEach(([k, v]) => {
        if (k === "score" || v == null) return;
        if (Array.isArray(v)) {
          if (v.length === 0) return;
          writeLine(`${k}:`, { size: 10, bold: true });
          v.forEach(item => {
            if (typeof item === "object") return;
            writeLine(`  • ${item}`, { size: 10 });
          });
        } else if (typeof v === "string" && v) {
          writeLine(`${k}: ${v}`, { size: 10 });
        } else if (typeof v === "boolean") {
          writeLine(`${k}: ${v ? "Ja" : "Nein"}`, { size: 10 });
        }
      });
    });

    // Checklist
    if (ai.checklist?.length) {
      doc.addPage(); y = margin;
      writeLine("Deine Checkliste", { size: 16, bold: true, color: [236, 72, 153] });
      y += 4;
      ai.checklist.forEach((c, i) => {
        const isChecked = checked[i];
        const box = isChecked ? "[x]" : "[ ]";
        writeLine(`${box}  ${c.category} — ${c.task}`, { size: 11, bold: true });
        writeLine(`    Prioritaet: ${c.priority}  •  Impact: ${c.impact || ""}`, { size: 9, color: [100, 100, 100] });
        y += 2;
      });
    }

    // Action Plan
    if (ai.action_plan?.length) {
      y += 12;
      writeLine("Action Plan", { size: 14, bold: true, color: [236, 72, 153] });
      ai.action_plan.forEach((s, i) => writeLine(`${i + 1}. ${s}`, { size: 10 }));
    }

    doc.save(`viralcut-audit-${profile.username}-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("PDF heruntergeladen!");
  };

  const fq = ai.follower_quality || {};
  const checklist = ai.checklist || [];
  const checkedCount = Object.values(checked).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#09090b]">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="h-8 w-8 text-zinc-400 hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Profil-Analyse</h1>
        </div>

        {/* Platform Selector + Search */}
        {!analysis?.status || analysis?.status === "error" ? (
          <div className="space-y-4">
            {analysis?.status === "error" && analysis?.error_message && (
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex gap-3" data-testid="analysis-error">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-300 mb-1">Analyse fehlgeschlagen</p>
                  <p className="text-xs text-red-200/80">{analysis.error_message}</p>
                </div>
              </div>
            )}
            <div className="flex gap-2" data-testid="platform-selector">
              {[
                { id: "instagram", label: "Instagram", icon: Instagram },
                { id: "tiktok", label: "TikTok", icon: Zap },
              ].map(p => (
                <button
                  key={p.id}
                  onClick={() => { setPlatform(p.id); setSuggestions([]); setQuery(""); }}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                    platform === p.id
                      ? "border-cyan-500 bg-cyan-500/10 text-cyan-400"
                      : "border-zinc-800 text-zinc-400 hover:border-zinc-700"
                  }`}
                  data-testid={`platform-${p.id}`}
                >
                  <p.icon className="w-4 h-4" />
                  {p.label}
                </button>
              ))}
            </div>

            {dna && <BrandDnaCard summary={dna} variant="hint" />}

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`${platform === "instagram" ? "Instagram" : "TikTok"}-Username eingeben...`}
                className="bg-zinc-900 border-zinc-800 text-white pl-10 h-11 pr-20"
                data-testid="search-input"
              />
              <Button
                onClick={searchProfiles}
                disabled={searching || query.length < 2}
                className="absolute right-1 top-1/2 -translate-y-1/2 bg-cyan-500 hover:bg-cyan-400 text-white h-8 px-3 text-xs"
                data-testid="search-btn"
              >
                {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : "Suchen"}
              </Button>

              {suggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 rounded-xl bg-zinc-900 border border-zinc-800 shadow-xl overflow-hidden" data-testid="suggestions-dropdown">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => startAnalysis(s.username)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 transition-colors text-left"
                      data-testid={`suggestion-${i}`}
                    >
                      {s.profile_pic ? (
                        <img src={proxyImage(s.profile_pic)} alt="" className="w-9 h-9 rounded-full object-cover border border-zinc-700" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-zinc-800 border border-zinc-700" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white truncate">@{s.username}</p>
                        <p className="text-xs text-zinc-500 truncate">{s.full_name}</p>
                      </div>
                      {s.is_verified && <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={() => query.length >= 2 && startAnalysis(query.trim().replace(/^@/, ""))}
                disabled={loading || query.length < 2}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-lime-400 hover:from-cyan-400 hover:to-lime-300 text-white h-10 text-sm font-semibold"
                data-testid="direct-analyse-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Direkt analysieren →</>}
              </Button>
            </div>

            <p className="text-xs text-zinc-500">
              Tipp: Username eingeben (z.B. <span className="text-zinc-300">erfolg_beginnt_in_dir</span>) und auf <span className="text-cyan-400">"Direkt analysieren"</span> klicken — schnellster Weg zur kompletten Analyse.
            </p>

            {loading && (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-3" />
                <p className="text-sm text-zinc-400">Profil wird geladen...</p>
              </div>
            )}
          </div>
        ) : analysis.status === "scraping" || analysis.status === "analyzing" ? (
          <div className="text-center py-12">
            <Loader2 className="w-10 h-10 text-cyan-400 animate-spin mx-auto mb-4" />
            <p className="text-base font-medium text-white mb-2">
              {analysis.status === "scraping" ? "Profil wird gescraped..." : "KI analysiert das Profil..."}
            </p>
            <Progress value={analysis.progress || 0} className="h-1.5 max-w-xs mx-auto mb-2" />
            <p className="text-xs text-zinc-500">@{analysis.username} auf {analysis.platform}</p>
          </div>
        ) : analysis.status === "completed" && ai ? (
          <div className="space-y-4">
            {/* Profile Header */}
            <div className="flex items-center gap-4 p-4 rounded-xl bg-zinc-900 border border-zinc-800" data-testid="profile-header">
              {profile.profile_pic ? (
                <img src={proxyImage(profile.profile_pic)} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-zinc-700" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-zinc-800 border-2 border-zinc-700" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-lg font-bold text-white truncate flex items-center gap-1.5" style={{ fontFamily: 'Outfit' }}>
                  @{profile.username}
                  {profile.is_verified && <CheckCircle2 className="w-4 h-4 text-cyan-400" />}
                </p>
                <p className="text-sm text-zinc-400 truncate">{profile.full_name}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-zinc-500">
                  <span><strong className="text-white">{(profile.followers || 0).toLocaleString()}</strong> Follower</span>
                  <span><strong className="text-white">{(profile.following || 0).toLocaleString()}</strong> Following</span>
                  <span><strong className="text-white">{(profile.posts_count || profile.videos_count || 0).toLocaleString()}</strong> Beitraege</span>
                </div>
              </div>
              <ScoreCircle score={ai.overall_score || 0} />
            </div>

            {/* Action toolbar */}
            <div className="flex flex-wrap gap-2">
              <Button onClick={downloadPdf} variant="outline" size="sm" className="border-zinc-700 text-zinc-300 hover:text-white" data-testid="download-pdf-btn">
                <FileText className="w-3.5 h-3.5 mr-1.5" /> Als PDF speichern
              </Button>
              <Button onClick={() => { setAnalysis(null); setAnalysisId(null); setQuery(""); setChecked({}); setCompetitors(null); setCompareId(null); setCompare(null); }} variant="outline" size="sm" className="border-zinc-700 text-zinc-300 hover:text-white" data-testid="new-analysis-btn">
                Neue Analyse
              </Button>
            </div>

            {/* Overall Verdict */}
            <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
              <p className="text-sm text-zinc-300 leading-relaxed">{ai.overall_verdict}</p>
            </div>

            {/* Follower Quality / Ghost Follower */}
            {fq && (fq.ghost_follower_pct !== undefined || fq.engagement_rate !== undefined) && (
              <Section title="Follower-Qualität & Ghost-Follower" icon={Ghost} defaultOpen={true}>
                <div className="flex items-center gap-3 mb-3">
                  <ScoreCircle score={fq.score || 0} size="sm" />
                  <div className="flex-1">
                    <p className="text-xs text-zinc-500">Engagement-Rate</p>
                    <p className="text-base font-bold text-white">{fq.engagement_rate}% <span className="text-xs text-zinc-500 font-normal">/ Benchmark {fq.benchmark_er}%</span></p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                    <p className="text-[10px] uppercase tracking-wider text-red-400 mb-1 flex items-center gap-1">
                      <Ghost className="w-3 h-3" /> Ghost / Inaktiv
                    </p>
                    <p className="text-lg font-bold text-red-300">{fq.ghost_follower_pct}%</p>
                    <p className="text-xs text-red-300/70">~{(fq.ghost_follower_estimate || 0).toLocaleString()} Accounts</p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                    <p className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1 flex items-center gap-1">
                      <Users className="w-3 h-3" /> Aktive Follower
                    </p>
                    <p className="text-lg font-bold text-emerald-300">{(fq.active_followers_estimate || 0).toLocaleString()}</p>
                    <p className="text-xs text-emerald-300/70">echte Reichweite</p>
                  </div>
                </div>
                {fq.verdict && <p className="text-xs text-zinc-300 mt-2 leading-relaxed">{fq.verdict}</p>}
                {fq.warning && (
                  <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 flex gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-300/90">{fq.warning}</p>
                  </div>
                )}
                {fq.recommendations?.length > 0 && <><p className="text-xs text-cyan-400 font-medium mb-1 mt-2">Empfehlungen:</p><ListItems items={fq.recommendations} color="text-zinc-300" /></>}
              </Section>
            )}

            {/* ──── PREMIUM-CONTENT BLOCK (Bio + Content + Hashtags + Compliance + Algorithm + Growth + Checklist + Action Plan) ──── */}
            <div className={`relative ${isFreeTrialMode ? "max-h-[500px] overflow-hidden" : ""}`} data-testid="premium-content-block">
              <div className={isFreeTrialMode ? "blur-sm pointer-events-none select-none space-y-4" : "space-y-4"}>
                {/* Bio Analysis */}
                <Section title="Bio & Profil" icon={Star}>
              <div className="flex items-center gap-3 mb-2">
                <ScoreCircle score={ai.bio_analysis?.score || 0} size="sm" />
                <div><p className="text-xs text-zinc-500">Bio-Score</p></div>
              </div>
              {ai.bio_analysis?.strengths?.length > 0 && <><p className="text-xs text-emerald-400 font-medium mb-1">Stärken:</p><ListItems items={ai.bio_analysis.strengths} color="text-emerald-300/80" /></>}
              {ai.bio_analysis?.weaknesses?.length > 0 && <><p className="text-xs text-red-400 font-medium mb-1 mt-2">Schwächen:</p><ListItems items={ai.bio_analysis.weaknesses} color="text-red-300/80" /></>}
              {ai.bio_analysis?.cta_quality && <p className="text-xs text-zinc-300 mt-2"><span className="text-cyan-400">CTA:</span> {ai.bio_analysis.cta_quality}</p>}
              {ai.bio_analysis?.optimized_bio && (
                <div className="mt-3 p-3 rounded-lg bg-zinc-950 border border-zinc-800">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Optimierte Bio (Copy & Paste):</p>
                  <p className="text-xs text-cyan-300 whitespace-pre-wrap">{ai.bio_analysis.optimized_bio}</p>
                </div>
              )}
            </Section>

            {/* Content Analysis */}
            <Section title="Content & Engagement" icon={TrendingUp}>
              <div className="flex items-center gap-3 mb-2">
                <ScoreCircle score={ai.content_analysis?.score || 0} size="sm" />
                <p className="text-xs text-zinc-400">{ai.content_analysis?.engagement_rate_label || ai.content_analysis?.engagement_rate}</p>
              </div>
              {ai.content_analysis?.top_performing && <p className="text-xs text-zinc-300"><span className="text-emerald-400">Bester Post:</span> {ai.content_analysis.top_performing}</p>}
              {ai.content_analysis?.worst_performing && <p className="text-xs text-zinc-300 mt-1"><span className="text-red-400">Schlechtester:</span> {ai.content_analysis.worst_performing}</p>}
              {ai.content_analysis?.hook_quality && <p className="text-xs text-zinc-300 mt-1"><span className="text-cyan-400">Hooks:</span> {ai.content_analysis.hook_quality}</p>}
              {ai.content_analysis?.caption_quality && <p className="text-xs text-zinc-300 mt-1"><span className="text-cyan-400">Captions:</span> {ai.content_analysis.caption_quality}</p>}
              {ai.content_analysis?.posting_consistency && <p className="text-xs text-zinc-300 mt-1"><span className="text-amber-400">Konsistenz:</span> {ai.content_analysis.posting_consistency}</p>}
              {ai.content_analysis?.best_format && <p className="text-xs text-zinc-300 mt-1"><span className="text-violet-400">Best-Format:</span> {ai.content_analysis.best_format}</p>}
            </Section>

            {/* Hashtag Analysis */}
            <Section title="Hashtag-Strategie" icon={Hash}>
              <div className="flex items-center gap-3 mb-2">
                <ScoreCircle score={ai.hashtag_analysis?.score || 0} size="sm" />
              </div>
              {ai.hashtag_analysis?.current_strategy && <p className="text-xs text-zinc-300">{ai.hashtag_analysis.current_strategy}</p>}
              {ai.hashtag_analysis?.recommended_top5?.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-emerald-400 font-medium mb-1">Top 5 Hashtags:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ai.hashtag_analysis.recommended_top5.map((h, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-400 text-xs">#{h.replace('#','')}</span>
                    ))}
                  </div>
                </div>
              )}
              {ai.hashtag_analysis?.niche_hashtags?.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-cyan-400 font-medium mb-1">Nischen-Hashtags:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ai.hashtag_analysis.niche_hashtags.map((h, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-md bg-cyan-500/10 text-cyan-400 text-xs">#{h.replace('#','')}</span>
                    ))}
                  </div>
                </div>
              )}
              {ai.hashtag_analysis?.hashtags_to_avoid?.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-red-400 font-medium mb-1">Vermeiden:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ai.hashtag_analysis.hashtags_to_avoid.map((h, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-md bg-red-500/10 text-red-400 text-xs line-through">#{h.replace('#','')}</span>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* Compliance Check */}
            <Section title="Compliance & Richtlinien" icon={Shield}>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <ScoreCircle score={ai.compliance_check?.score || 0} size="sm" />
                {ai.compliance_check?.engagement_bait_detected && <span className="text-xs text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Engagement Bait</span>}
                {ai.compliance_check?.clickbait_detected && <span className="text-xs text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Clickbait</span>}
                {ai.compliance_check?.shadowban_risk && <span className="text-xs text-zinc-400">Shadowban-Risiko: <span className="text-amber-400">{ai.compliance_check.shadowban_risk}</span></span>}
              </div>
              {ai.compliance_check?.violations_found?.length > 0 && <><p className="text-xs text-red-400 font-medium mb-1">Verstöße:</p><ListItems items={ai.compliance_check.violations_found} color="text-red-300/80" /></>}
              {ai.compliance_check?.scam_signals?.length > 0 && <><p className="text-xs text-amber-400 font-medium mb-1 mt-2">Scam-Signale:</p><ListItems items={ai.compliance_check.scam_signals} color="text-amber-300/80" /></>}
              {ai.compliance_check?.recommendations?.length > 0 && <><p className="text-xs text-emerald-400 font-medium mb-1 mt-2">Empfehlungen:</p><ListItems items={ai.compliance_check.recommendations} color="text-emerald-300/80" /></>}
            </Section>

            {/* Algorithm Tips */}
            <Section title="Algorithmus & Viral-Tipps" icon={Zap}>
              {ai.algorithm_tips?.topic_clusters?.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs text-zinc-500 mb-1">Topic Clusters:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ai.algorithm_tips.topic_clusters.map((c, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-md bg-cyan-500/10 text-cyan-400 text-xs">{c}</span>
                    ))}
                  </div>
                </div>
              )}
              {ai.algorithm_tips?.more_of?.length > 0 && (
                <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 mb-2">
                  <p className="text-xs text-emerald-400 font-medium mb-1">More of (Your Algorithm):</p>
                  <p className="text-xs text-emerald-300/80">{ai.algorithm_tips.more_of.join(", ")}</p>
                </div>
              )}
              {ai.algorithm_tips?.less_of?.length > 0 && (
                <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20 mb-2">
                  <p className="text-xs text-red-400 font-medium mb-1">Less of (Your Algorithm):</p>
                  <p className="text-xs text-red-300/80">{ai.algorithm_tips.less_of.join(", ")}</p>
                </div>
              )}
              {ai.algorithm_tips?.viral_tips?.length > 0 && <><p className="text-xs text-cyan-400 font-medium mb-1">Viral-Tipps:</p><ListItems items={ai.algorithm_tips.viral_tips} color="text-zinc-300" /></>}
              {ai.algorithm_tips?.reels_strategy && <p className="text-xs text-zinc-300 mt-2"><span className="text-violet-400">Reels-Strategie:</span> {ai.algorithm_tips.reels_strategy}</p>}
              {ai.algorithm_tips?.best_posting_times && <p className="text-xs text-zinc-300 mt-2"><span className="text-cyan-400">Posting-Zeiten:</span> {ai.algorithm_tips.best_posting_times}</p>}
            </Section>

            {/* Growth Strategy */}
            <Section title="Wachstumsstrategie" icon={Target}>
              {ai.growth_strategy?.current_trajectory && <p className="text-xs text-zinc-300 mb-2">{ai.growth_strategy.current_trajectory}</p>}
              {ai.growth_strategy?.quick_wins?.length > 0 && <><p className="text-xs text-emerald-400 font-medium mb-1">Quick Wins:</p><ListItems items={ai.growth_strategy.quick_wins} color="text-emerald-300/80" /></>}
              {ai.growth_strategy?.long_term?.length > 0 && <><p className="text-xs text-cyan-400 font-medium mb-1 mt-2">Langfristig:</p><ListItems items={ai.growth_strategy.long_term} color="text-cyan-300/80" /></>}
              {ai.growth_strategy?.competitor_tip && <p className="text-xs text-zinc-300 mt-2"><span className="text-cyan-400">Konkurrenz:</span> {ai.growth_strategy.competitor_tip}</p>}
              {ai.growth_strategy?.monetization_potential && <p className="text-xs text-zinc-300 mt-1"><span className="text-amber-400">Monetarisierung:</span> {ai.growth_strategy.monetization_potential}</p>}
            </Section>

            {/* Interactive Checklist */}
            {checklist.length > 0 && (
              <Section title={`Deine Checkliste (${checkedCount}/${checklist.length})`} icon={CheckCircle2} defaultOpen={true}>
                <Progress value={checklist.length ? (checkedCount / checklist.length) * 100 : 0} className="h-1 mb-3" />
                <div className="space-y-2" data-testid="checklist">
                  {checklist.map((c, i) => (
                    <label
                      key={i}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        checked[i]
                          ? "bg-emerald-500/5 border-emerald-500/30"
                          : "bg-zinc-950 border-zinc-800 hover:border-zinc-700"
                      }`}
                      data-testid={`checklist-item-${i}`}
                    >
                      <Checkbox
                        checked={!!checked[i]}
                        onCheckedChange={(v) => setChecked(prev => ({ ...prev, [i]: !!v }))}
                        className="mt-0.5 border-zinc-600 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-[10px] uppercase tracking-wider text-zinc-500">{c.category}</span>
                          <PriorityBadge priority={c.priority} />
                        </div>
                        <p className={`text-xs leading-relaxed ${checked[i] ? "text-zinc-500 line-through" : "text-zinc-200"}`}>{c.task}</p>
                        {c.impact && <p className="text-[10px] text-zinc-500 mt-1">→ {c.impact}</p>}
                      </div>
                    </label>
                  ))}
                </div>
                <Button onClick={downloadPdf} variant="outline" size="sm" className="w-full mt-3 border-zinc-700 text-zinc-300 hover:text-white">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Checkliste & Audit als PDF
                </Button>
              </Section>
            )}

            {/* Action Plan */}
            {ai.action_plan?.length > 0 && (
              <Section title="Dein Aktionsplan" icon={CheckCircle2}>
                <div className="space-y-2">
                  {ai.action_plan.map((step, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-zinc-950 border border-zinc-800">
                      <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-cyan-400">{i + 1}</span>
                      </div>
                      <p className="text-xs text-zinc-300">{step}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}
              </div>
              {isFreeTrialMode && <PaywallOverlay />}
            </div>
            {/* ──── END PREMIUM BLOCK ──── */}

            {!isFreeTrialMode && (
              <Button onClick={() => { setAnalysis(null); setAnalysisId(null); setQuery(""); setChecked({}); setCompetitors(null); setCompareId(null); setCompare(null); }} variant="outline" className="w-full border-zinc-700 text-zinc-400" data-testid="new-analysis-btn-bottom">
                Neue Analyse starten
              </Button>
            )}

            {!isFreeTrialMode && (
            <div className="pt-4 border-t border-zinc-800 mt-6">
              <div className="flex items-center gap-2 mb-3">
                <Swords className="w-5 h-5 text-cyan-400" />
                <h2 className="text-base font-bold text-white" style={{ fontFamily: 'Outfit' }}>Konkurrenz-Vergleich</h2>
                <span className="text-[10px] uppercase tracking-wider text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded-md border border-cyan-500/30">Premium</span>
              </div>
              <p className="text-xs text-zinc-500 mb-3">
                Lass die KI 5 erfolgreiche Accounts in deiner Nische vorschlagen — wähle einen aus und bekomme einen knallharten Head-to-Head-Audit: was er besser macht und wie du ihn überholst.
              </p>

              {!competitors && !compareId && (
                <Button onClick={loadCompetitors} disabled={loadingCompetitors} className="w-full bg-gradient-to-r from-cyan-500 to-lime-400 hover:from-cyan-400 hover:to-lime-300 text-white" data-testid="load-competitors-btn">
                  {loadingCompetitors ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Konkurrent:innen werden gesucht...</> : <><Sparkles className="w-4 h-4 mr-2" />Konkurrent:innen vorschlagen lassen</>}
                </Button>
              )}

              {competitors && !compareId && (
                <div className="space-y-2" data-testid="competitor-suggestions">
                  {competitors.note && (
                    <div className="text-[10px] text-zinc-500 italic mb-2">{competitors.note}</div>
                  )}
                  {(competitors.competitors || []).map((c, i) => (
                    <div key={i} className="p-3 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-cyan-500/40 transition-colors" data-testid={`competitor-${i}`}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold text-white">@{c.username}</p>
                          <p className="text-xs text-zinc-400">{c.display_name}</p>
                          <span className={`inline-block mt-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                            c.size_tier === "gross" ? "bg-cyan-500/10 text-cyan-400 border-cyan-500/30" :
                            c.size_tier === "mittel" ? "bg-cyan-500/10 text-cyan-400 border-cyan-500/30" :
                            "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                          }`}>{c.size_tier}</span>
                        </div>
                        <Button onClick={() => startCompare(c.username)} disabled={loadingCompare} size="sm" className="bg-cyan-500 hover:bg-cyan-400 text-white text-xs h-8" data-testid={`compare-btn-${i}`}>
                          {loadingCompare ? <Loader2 className="w-3 h-3 animate-spin" /> : <>Vergleichen <ArrowUpRight className="w-3 h-3 ml-1" /></>}
                        </Button>
                      </div>
                      {c.reason_to_study && <p className="text-xs text-zinc-300 mb-1"><span className="text-cyan-400">Warum:</span> {c.reason_to_study}</p>}
                      {c.what_they_do_well && <p className="text-[11px] text-zinc-400 italic">→ {c.what_they_do_well}</p>}
                    </div>
                  ))}
                  <Button onClick={() => setCompetitors(null)} variant="outline" size="sm" className="w-full border-zinc-700 text-zinc-400 text-xs">Zurück</Button>
                </div>
              )}

              {compareId && compare && (
                <CompareView compare={compare} onReset={() => { setCompareId(null); setCompare(null); }} />
              )}
            </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ─── Compare View ───────────────────────────────────────────
function CompareView({ compare, onReset }) {
  const cr = compare.compare_result || {};
  const sb = cr.scoreboard || {};
  const youUsername = compare.you_username;
  const compUsername = compare.competitor_username;

  if (compare.status === "scraping" || compare.status === "analyzing") {
    return (
      <div className="text-center py-10" data-testid="compare-loading">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-3" />
        <p className="text-sm font-medium text-white mb-1">
          {compare.status === "scraping" ? "Konkurrent wird gescraped..." : "KI vergleicht beide Profile..."}
        </p>
        <Progress value={compare.progress || 0} className="h-1 max-w-xs mx-auto mb-2" />
        <p className="text-xs text-zinc-500">@{youUsername} <span className="text-cyan-400 mx-2">vs</span> @{compUsername}</p>
      </div>
    );
  }

  if (compare.status === "error") {
    return (
      <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex gap-3" data-testid="compare-error">
        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-red-300 mb-1">Vergleich fehlgeschlagen</p>
          <p className="text-xs text-red-200/80 mb-2">{compare.error_message || "Unbekannter Fehler"}</p>
          <Button onClick={onReset} variant="outline" size="sm" className="border-red-500/40 text-red-300 hover:bg-red-500/10">Zurück</Button>
        </div>
      </div>
    );
  }

  const winner = cr.winner;
  return (
    <div className="space-y-3" data-testid="compare-result">
      {/* Header */}
      <div className="p-4 rounded-xl bg-gradient-to-br from-cyan-500/10 to-lime-400/5 border border-cyan-500/30">
        <div className="flex items-center justify-between gap-3 mb-2">
          <span className={`text-sm font-bold ${winner === "you" ? "text-emerald-400" : "text-zinc-300"}`}>@{youUsername}</span>
          <Swords className="w-5 h-5 text-cyan-400" />
          <span className={`text-sm font-bold ${winner === "competitor" ? "text-emerald-400" : "text-zinc-300"}`}>@{compUsername}</span>
        </div>
        <div className="flex justify-center mb-2">
          {winner === "you" && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center gap-1"><Trophy className="w-3 h-3" />Du führst</span>}
          {winner === "competitor" && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">Konkurrent führt</span>}
          {winner === "tied" && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-zinc-500/15 text-zinc-400 border border-zinc-500/30">Unentschieden</span>}
        </div>
        <p className="text-xs text-zinc-300 leading-relaxed text-center">{cr.summary}</p>
      </div>

      {/* Scoreboard */}
      <div className="rounded-xl bg-zinc-900 border border-zinc-800 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
          <Trophy className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-semibold text-white">Scoreboard</span>
        </div>
        <div className="divide-y divide-zinc-800">
          {[
            { key: "bio", label: "Bio & Profil" },
            { key: "content", label: "Content" },
            { key: "hooks", label: "Hooks" },
            { key: "engagement", label: "Engagement" },
            { key: "hashtags", label: "Hashtags" },
            { key: "branding", label: "Branding" },
          ].map(({ key, label }) => {
            const w = sb[`${key}_winner`];
            const r = sb[`${key}_reason`];
            return (
              <div key={key} className="px-4 py-2.5 flex items-start gap-3">
                <div className="w-24 flex-shrink-0">
                  <p className="text-xs text-zinc-400">{label}</p>
                  <p className={`text-xs font-bold ${w === "you" ? "text-emerald-400" : "text-amber-400"}`}>
                    {w === "you" ? "Du" : "Konkurrent"} ✓
                  </p>
                </div>
                <p className="text-xs text-zinc-300 flex-1">{r}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* What competitor does better */}
      {cr.what_competitor_does_better?.length > 0 && (
        <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
          <div className="flex items-center gap-2 mb-2">
            <ArrowUpRight className="w-4 h-4 text-amber-400" />
            <p className="text-sm font-semibold text-amber-300">Was der Konkurrent besser macht</p>
          </div>
          <ul className="space-y-1.5">
            {cr.what_competitor_does_better.map((p, i) => (
              <li key={i} className="text-xs text-amber-200/90 flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">▸</span><span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* What you do better */}
      {cr.what_you_do_better?.length > 0 && (
        <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-4 h-4 text-emerald-400" />
            <p className="text-sm font-semibold text-emerald-300">Wo du bereits vorn bist</p>
          </div>
          <ul className="space-y-1.5">
            {cr.what_you_do_better.map((p, i) => (
              <li key={i} className="text-xs text-emerald-200/90 flex items-start gap-2">
                <span className="text-emerald-500 mt-0.5">▸</span><span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Steal these strategies */}
      {cr.steal_these_strategies?.length > 0 && (
        <div className="rounded-xl bg-zinc-900 border border-zinc-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-semibold text-white">Klauen & Anwenden</span>
          </div>
          <div className="divide-y divide-zinc-800">
            {cr.steal_these_strategies.map((s, i) => (
              <div key={i} className="px-4 py-3">
                <p className="text-xs font-medium text-cyan-300 mb-1">→ {s.strategy}</p>
                <p className="text-xs text-zinc-400 pl-3">{s.how_to_apply}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Outperform plan */}
      {cr.outperform_plan?.length > 0 && (
        <div className="p-4 rounded-xl bg-gradient-to-br from-cyan-500/10 to-lime-400/5 border border-cyan-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-cyan-400" />
            <p className="text-sm font-semibold text-cyan-300">Dein 90-Tage Überhol-Plan</p>
          </div>
          <ol className="space-y-1.5">
            {cr.outperform_plan.map((s, i) => (
              <li key={i} className="text-xs text-zinc-200 flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-400 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                <span className="leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {cr.differentiation_angle && (
        <div className="p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
          <p className="text-xs font-medium text-cyan-300 mb-1">Dein Unique Angle</p>
          <p className="text-xs text-cyan-200/80">{cr.differentiation_angle}</p>
        </div>
      )}

      {cr.warning && (
        <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20 flex gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-200/90">{cr.warning}</p>
        </div>
      )}

      <Button onClick={onReset} variant="outline" size="sm" className="w-full border-zinc-700 text-zinc-400">
        Anderen Konkurrent vergleichen
      </Button>
    </div>
  );
}
